import { Component, TemplateRef, computed, input, output, viewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  EHeaderClasses,
  ICustomColumn,
  IDatatableConfiguration,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { GroupedDatatableComponent } from '@shared/components/grouped-datatable/grouped-datatable.component';
import {
  IGroupedDatatableColumn,
  IGroupedDatatableConfiguration,
  IGroupedDatatableCustomColumn,
} from '@shared/components/grouped-datatable/grouped-datatable.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { IGroupedSkeletonLoaderConfig } from '@shared/components/skeleton-loader/grouped-skeleton-loader.interface';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { ECampaignDetailsTab } from '../../campaign-details.interface';
import { ICampaignExportConfig } from '../../campaign-export-config/campaign-export-config.interface';
import {
  ICampaignDetailsObservationsGroupedChildRow,
  ICampaignDetailsObservationsGroupedParentRow,
  ICampaignDetailsObservationsTableRow,
  TCampaignDetailsObservationsGroupedRow,
} from '../campaign-details-observations.interface';

@Component({
  imports: [
    DatatableComponent,
    GroupedDatatableComponent,
    IconBadgeComponent,
    IconComponent,
    MatTooltipModule,
    StatusBadgeComponent,
    TextClampTooltipDirective,
  ],
  selector: 'app-campaign-details-observations-table',
  styleUrl: './campaign-details-observations-table.component.scss',
  templateUrl: './campaign-details-observations-table.component.html',
})
export class CampaignDetailsObservationsTableComponent {
  protected readonly currentTab = toEventFrontendTab(ECampaignDetailsTab.observations);

  public readonly campaignExportConfig = input.required<ICampaignExportConfig>();

  public readonly groupedMode = input<boolean>(false);
  public readonly groupedRowData = input<TCampaignDetailsObservationsGroupedRow[]>([]);
  public readonly loading = input<boolean>(false);
  public readonly rowData = input<ICampaignDetailsObservationsTableRow[]>([]);

  public readonly rowClicked = output<ICampaignDetailsObservationsTableRow>();

  public readonly groupedSkeletonLoaderConfig: IGroupedSkeletonLoaderConfig = {
    childColumns: [
      {
        borderRadius: '4px',
        circleSize: '14px',
        className: 'min-w-0',
        type: 'icon',
      },
      {
        borderRadius: '100vw',
        className: 'min-w-0',
        columnPadding: '8px 0 8px 16px',
        height: '24px',
        textWidth: '90px',
        type: 'text',
      },
      {
        borderRadius: '4px',
        className: 'min-w-0',
        height: '20px',
        textWidth: '160px',
        type: 'text',
      },
    ],
    childIndentPx: 0,
    childRowGridTemplateColumns: '40px 132px minmax(0,1fr)',
    childRowsPerGroup: 2,
    childWrapperClass: 'box-border min-w-0',
    childWrapperStyle: {
      marginLeft: '30%',
      width: 'calc(70% - 8px)',
    },
    detailMode: 'expanded',
    expandColumn: { className: 'hidden' },
    groupCount: 5,
    headerMargin: '0',
    headerPadding: '0 16px',
    parentColumns: [
      {
        borderRadius: '4px',
        circleSize: '24px',
        className: 'min-w-0',
        hasText: true,
        iconRadius: '100px',
        textWidth: '96px',
        type: 'icon',
      },
      { borderRadius: '4px', className: 'min-w-0', height: '20px', textWidth: '100px', type: 'text' },
      { borderRadius: '100vw', className: 'min-w-0', height: '20px', textWidth: '40px', type: 'text' },
    ],
    parentGridTemplateColumns: 'minmax(0,30%) minmax(0,20%) minmax(0,12%) 40px',
    rowSeparatorEnabled: true,
    showColumns: true,
    showExpandAllTogglePlaceholder: true,
    showPaginator: true,
    showSearchField: false,
  };

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '100vw',
        className: 'w-[12.5%]',
        height: '24px',
        rowPadding: '16px 0',
        textWidth: '91px',
        type: 'text',
      },
      { borderRadius: '4px', className: 'w-[35%]', height: '20px', textWidth: '180px', type: 'text' },
      {
        borderRadius: '4px',
        circleSize: '24px',
        className: 'w-[35%]',
        hasText: true,
        iconRadius: '100px',
        textWidth: '120px',
        type: 'icon',
      },
      { borderRadius: '4px', className: 'w-[17.5%]', height: '20px', textWidth: '100px', type: 'text' },
    ],
    rows: 5,
    showColumns: true,
    showHeaderControls: false,
    showSearchField: false,
  };

  public readonly creatorColumnTemplate = viewChild.required<TemplateRef<unknown>>('creatorColumn');
  public readonly dateColumnTemplate = viewChild.required<TemplateRef<unknown>>('dateColumn');
  public readonly observationsCountColumnTemplate = viewChild.required<TemplateRef<unknown>>('observationsCountColumn');
  public readonly groupedChildIndicatorColumnTemplate =
    viewChild.required<TemplateRef<unknown>>('groupedChildIndicatorColumn');
  public readonly groupedObservationColumnTemplate =
    viewChild.required<TemplateRef<unknown>>('groupedObservationColumn');
  public readonly statusColumnTemplate = viewChild.required<TemplateRef<unknown>>('statusColumn');
  public readonly observationColumnTemplate = viewChild.required<TemplateRef<unknown>>('observationColumn');

  public readonly columns: TDatatableColumn<ICampaignDetailsObservationsTableRow, string>[] = [
    {
      dataViewStrategy: (row) => row.statusSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'status',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.deliverableName,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'observation',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.creatorName,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'creator',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.dateSortValue,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'date',
      isSortable: true,
    },
  ];

  public readonly configuration = computed<IDatatableConfiguration<ICampaignDetailsObservationsTableRow>>(() => ({
    defaultSort: { active: 'date', direction: 'desc' },
    isClientSide: true,
    isLoaderEnabled: true,
    isRowClickEnabled: this.campaignExportConfig().allowObservationsRowClick,
    isSearchEnabled: false,
    pageSizeOptions: [5, 10, 25, 50],
    skipInitialRequest: true,
    storageKey: 'campaign-details-observations-table',
  }));

  public readonly customColumns = computed<ICustomColumn[]>(() => [
    { isSortable: true, name: 'status', template: this.statusColumnTemplate() },
    { isSortable: true, name: 'observation', template: this.observationColumnTemplate() },
    { isSortable: true, name: 'creator', template: this.creatorColumnTemplate() },
    { isSortable: true, name: 'date', template: this.dateColumnTemplate() },
  ]);

  public readonly groupedParentColumns = computed<
    IGroupedDatatableColumn<ICampaignDetailsObservationsGroupedParentRow>[]
  >(() => [
    {
      id: 'creator',
      isSearchable: true,
      isSortable: true,
      label: 'field.creator',
      valueGetter: (row): string => row.creatorName,
    },
    {
      id: 'date',
      isSearchable: true,
      isSortable: true,
      label: 'field.date',
      valueGetter: (row): number => row.dateSortValue,
    },
    {
      id: 'observations',
      isSearchable: false,
      isSortable: true,
      label: 'field.observations',
      valueGetter: (row): number => row.observationsCount,
    },
  ]);

  public readonly groupedChildColumns = computed<
    IGroupedDatatableColumn<ICampaignDetailsObservationsGroupedChildRow>[]
  >(() => [
    {
      id: 'childIndicator',
      isSearchable: false,
      isSortable: false,
      label: '',
      valueGetter: (): string => '',
    },
    {
      id: 'status',
      isSearchable: true,
      isSortable: false,
      label: 'field.status',
      valueGetter: (row): string => row.rowStatus as string,
    },
    {
      id: 'observation',
      isSearchable: true,
      isSortable: false,
      label: 'field.observation',
      valueGetter: (row): string => row.sourceRow.deliverableName,
    },
  ]);

  public readonly groupedParentCustomColumns = computed<
    IGroupedDatatableCustomColumn<ICampaignDetailsObservationsGroupedParentRow>[]
  >(() => [
    { id: 'creator', template: this.creatorColumnTemplate() },
    { id: 'date', template: this.dateColumnTemplate() },
    { id: 'observations', template: this.observationsCountColumnTemplate() },
  ]);

  public readonly groupedChildCustomColumns = computed<
    IGroupedDatatableCustomColumn<ICampaignDetailsObservationsGroupedChildRow>[]
  >(() => [
    { id: 'childIndicator', template: this.groupedChildIndicatorColumnTemplate() },
    { id: 'status', template: this.statusColumnTemplate() },
    { id: 'observation', template: this.groupedObservationColumnTemplate() },
  ]);

  public readonly groupedConfiguration = computed<
    IGroupedDatatableConfiguration<
      ICampaignDetailsObservationsGroupedParentRow,
      ICampaignDetailsObservationsGroupedChildRow
    >
  >(() => ({
    childIndentPx: 0,
    defaultSort: { active: 'date', direction: 'desc' },
    hideParentsWithoutVisibleChildren: true,
    isChildHeaderEnabled: false,
    isChildRowClickEnabled: this.campaignExportConfig().allowObservationsRowClick,
    isPaginationEnabled: true,
    isParentRowClickEnabled: true,
    isParentRowToggleEnabled: true,
    isSearchEnabled: false,
    pageSizeOptions: [5, 10, 25, 50],
    parentSortAccessor: (parent, sortKey): Date | number | string | null | undefined => {
      if (sortKey === 'creator') {
        return parent.creatorName;
      }

      if (sortKey === 'date') {
        return parent.dateSortValue;
      }

      if (sortKey === 'observations') {
        return parent.observationsCount;
      }

      return '';
    },
    preserveExpandStateOnFilter: true,
    rowSeparatorEnabled: true,
    showExpandAllToggle: true,
    storageKey: 'campaign-details-observations-grouped-table',
  }));

  public onGroupedChildRowClicked(row: ICampaignDetailsObservationsGroupedChildRow): void {
    if (!this.campaignExportConfig().allowObservationsRowClick) {
      return;
    }

    this.rowClicked.emit(row.sourceRow);
  }
}
